"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wind, CircleCheck as CheckCircle2, RefreshCw, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function BlowerGuide() {
  const shouldReduceMotion = useReducedMotion();
  const points = [{
    title: "System Integration",
    icon: Wind,
    text: "The blower should be treated as part of the inflatable system rather than as an afterthought. Different inflatables can have different air requirements, so a blower that works with one arena may not automatically suit another."
  }, {
    title: "Confirm Before You Buy",
    icon: CheckCircle2,
    text: "Before buying, confirm whether the blower is included and review the requirements published for the exact inflatable."
  }, {
    title: "Used Equipment Checks",
    icon: RefreshCw,
    text: "If you are comparing a used inflatable mechanical bull, also check the condition of the blower and whether a compatible replacement can still be sourced if needed."
  }];
  return <section id="blowerinspectionguide" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="space-y-6">
          <h2 className="font-serif uppercase tracking-wide text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">
            Check the Inflatable Blower Before You Buy
          </h2>

          <p className="text-muted-foreground font-sans text-base sm:text-lg max-w-3xl leading-relaxed">
            The blower should be checked as part of the complete inflatable system because compatibility can vary between arenas.
          </p>
        </motion.div>

        <Separator className="my-10" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {points.map((point, index) => {
          const Icon = point.icon;
          return <motion.div key={point.title} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.12,
            ease: "easeOut"
          }}>
                <Card className="h-full border border-border bg-card rounded-none shadow-sm flex flex-col justify-between">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="size-10 bg-muted border border-border flex items-center justify-center mb-3">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <CardTitle className="font-serif uppercase tracking-wider text-xl font-bold text-card-foreground">
                        {point.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {point.text}
                      </p>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.35,
        ease: "easeOut"
      }} className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-muted/40 p-6 md:p-8">
          <div className="space-y-1">
            <h3 className="font-serif uppercase tracking-wider text-lg font-bold text-foreground">
              Need replacement blowers or compatible attachments?
            </h3>
            <p className="font-sans text-sm text-muted-foreground">
              Review current inventory and replacement hardware in our catalog.
            </p>
          </div>

          <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-serif uppercase tracking-wider font-semibold">
            <Link to="/MechanicalBullPartsAndAccessoriesPage" className="inline-flex items-center gap-2">
              <span>Browse Parts &amp; Accessories</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}